import type { FocusLabel, FocusPreference, FocusProject, FocusTask } from "./personalFocus";

export interface FocusDataAdapter {
  load: (workspaceId: string, userId: string, signal: AbortSignal) => Promise<{ tasks: FocusTask[]; preference: FocusPreference | null; labels?: FocusLabel[]; projects?: FocusProject[] }>;
  confirm: (workspaceId: string, userId: string, issueId: string, signal: AbortSignal) => Promise<FocusPreference>;
  clear: (workspaceId: string, userId: string, signal: AbortSignal) => Promise<void>;
}

export interface FocusSnapshot {
  tasks: readonly FocusTask[];
  labels: readonly FocusLabel[];
  projects: readonly FocusProject[];
  preference: FocusPreference | null;
  loaded: boolean;
  loading: boolean;
  saving: boolean;
  error: string | null;
  revision: number;
}

function accessDenied(error: unknown) {
  return error !== null && typeof error === "object" && "code" in error &&
    ["unauthorized", "unavailable", "session_changed", "invalid_response"].includes(String(error.code));
}

/** One controller belongs to one viewer/workspace. Never reuse it across scopes. */
export class PersonalFocusController {
  private snapshot: FocusSnapshot = { tasks: [], labels: [], projects: [], preference: null, loaded: false, loading: true, saving: false, error: null, revision: 0 };
  private listeners = new Set<() => void>();
  private observed = new Map<string, FocusTask>();
  private inventoryIds = new Set<string>();
  private inventoryTasks = new Map<string, FocusTask>();
  private read: AbortController | null = null;
  private write: AbortController | null = null;
  private generation = 0;
  private disposed = false;
  private pendingRefresh = false;

  constructor(readonly workspaceId: string, readonly userId: string, private adapter: FocusDataAdapter) {}

  getSnapshot = () => this.snapshot;
  subscribe = (listener: () => void) => { this.listeners.add(listener); return () => { this.listeners.delete(listener); }; };

  // React may replay an effect after cleanup in development StrictMode.
  activate() {
    if (!this.disposed) return;
    this.disposed = false;
    this.pendingRefresh = false;
  }

  invalidateRead() {
    if (this.disposed) return;
    this.generation++;
    this.read?.abort();
    if (this.snapshot.saving) this.pendingRefresh = true;
  }

  /** After a local write settles, its optimistic clock no longer authorizes an
   * overlay. The next complete personal read determines persisted state. */
  taskWriteSettled(issueId: string) {
    this.observed.delete(issueId);
    this.invalidateRead();
  }

  private publish(patch: Partial<FocusSnapshot>) {
    if (this.disposed) return;
    this.snapshot = { ...this.snapshot, ...patch };
    for (const listener of this.listeners) listener();
  }

  private merge(tasks: readonly FocusTask[]) {
    return tasks.map((task) => {
      const current = this.observed.get(task.id);
      // Bootstrap label links may be capped separately. The completed personal
      // query owns authoritative label membership after local writes settle.
      return current && current.updatedAt >= task.updatedAt ? { ...current, labelIds: task.labelIds } : task;
    });
  }

  /** Retain newer canonical status even if a capped store refresh drops its row. */
  observeInventory(tasks: readonly FocusTask[]): boolean {
    if (this.disposed) return false;
    const current = tasks.filter((task) => task.workspaceId === this.workspaceId);
    const ids = new Set(current.map((task) => task.id));
    const removed = [...this.inventoryIds].filter((id) => !ids.has(id));
    const replaced = new Set<string>();
    let changed = removed.length > 0;
    for (const task of current) {
      const previous = this.inventoryTasks.get(task.id);
      // A store refresh can roll back an unsuccessful optimistic edit (whose
      // client clock may be ahead). Accept that canonical replacement too.
      if (task !== previous) {
        this.observed.set(task.id, task);
        replaced.add(task.id);
        changed = true;
      }
    }
    this.inventoryIds = ids;
    this.inventoryTasks = new Map(current.map((task) => [task.id, task]));
    if (!changed) return false;
    // An absent row could be a local delete or a capped refresh. Hide it while
    // a complete personal read resolves that ambiguity; cancel older reads.
    if (removed.length) {
      this.generation++;
      this.read?.abort();
    }
    const removedIds = new Set(removed);
    this.publish({ tasks: this.snapshot.tasks.filter((task) => !removedIds.has(task.id)).map((task) =>
      replaced.has(task.id) ? this.observed.get(task.id)! : task) });
    return true;
  }

  async refresh() {
    if (this.disposed) return;
    if (this.snapshot.saving) { this.pendingRefresh = true; return; }
    this.read?.abort();
    const controller = new AbortController();
    this.read = controller;
    const generation = ++this.generation;
    this.publish({ loading: true });
    try {
      const result = await this.adapter.load(this.workspaceId, this.userId, controller.signal);
      if (this.disposed || generation !== this.generation || controller.signal.aborted) return;
      if ([...result.tasks, ...(result.labels ?? []), ...(result.projects ?? [])].some((row) => row.workspaceId !== this.workspaceId)) throw { code: "invalid_response" };
      this.publish({ tasks: this.merge(result.tasks), labels: result.labels ?? [], projects: result.projects ?? [], preference: result.preference, loaded: true, loading: false, error: null, revision: this.snapshot.revision + 1 });
    } catch (error) {
      if (this.disposed || generation !== this.generation || controller.signal.aborted) return;
      if (accessDenied(error)) {
        this.observed.clear();
        this.publish({ tasks: [], labels: [], projects: [], preference: null, loaded: false, loading: false, error: "Seu foco não está disponível nesta sessão ou workspace." });
        return;
      }
      this.publish({ loading: false, error: "Não foi possível atualizar seu foco. Tente novamente." });
    }
  }

  async confirm(issueId: string) { await this.save(issueId); }
  async clear() { await this.save(null); }

  private async save(issueId: string | null) {
    if (this.disposed || this.snapshot.saving) return;
    this.read?.abort();
    this.generation++;
    const controller = new AbortController();
    this.write = controller;
    this.publish({ saving: true, loading: false, error: null });
    try {
      const preference = issueId === null
        ? (await this.adapter.clear(this.workspaceId, this.userId, controller.signal), null)
        : await this.adapter.confirm(this.workspaceId, this.userId, issueId, controller.signal);
      if (this.disposed || controller.signal.aborted) return;
      this.publish({ preference, saving: false });
      this.pendingRefresh = false;
      await this.refresh();
    } catch (error) {
      if (this.disposed || controller.signal.aborted) return;
      if (accessDenied(error)) {
        this.observed.clear();
        this.publish({ tasks: [], labels: [], projects: [], preference: null, loaded: false, loading: false, saving: false, error: "Seu foco não está disponível nesta sessão ou workspace." });
        return;
      }
      this.publish({ saving: false, error: "Não foi possível salvar seu foco. Sua confirmação anterior foi mantida." });
      if (this.pendingRefresh) { this.pendingRefresh = false; await this.refresh(); }
    }
  }

  dispose() {
    if (this.disposed) return;
    this.publish({ tasks: [], labels: [], projects: [], preference: null, loaded: false, loading: true, saving: false, error: null });
    this.disposed = true;
    this.generation++;
    this.read?.abort();
    this.write?.abort();
    this.observed.clear();
    this.inventoryIds.clear();
    this.inventoryTasks.clear();
    this.listeners.clear();
  }
}
